import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgModule, OnDestroy, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Cms, TempAny } from '@impact/data';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ComponentLoaderModule } from '../../component-loader/component-loader.module';
import { chunksArray, sortObjectArrayByDateProperty } from '../../core/helpers';
import { SettingsService } from '../../core/settings.service';
import { StyleBreakpoints } from '../../models/style-breakpoints';
import { SectionHeaderModule } from '../../section-header/section-header.module';
import { IImageBreakpoints } from '../../utils/responsive-image.component';
import { UtilsModule } from '../../utils/utils.module';
import { ContentService } from '../../content/content.service';

const ARTICLE_ROWS_AMOUNT = 2;

export enum ArticleFilterTypes {
    CATEGORY = 'category',
    MAKE = 'make'
}

type ArticleGroup = Cms.ArticleDto[][];

@Component({
    selector: 'app-articles-spot',
    template: `
        <app-section-header
            [headerTextPrimary]="data?.articleSpotHeaderText"
            [buttonLink]="data?.articleSpotHeaderLink">
        </app-section-header>

        <ng-container [ngSwitch]="isFetchingArticles">
            <app-component-loader *ngSwitchCase="true"></app-component-loader>

            <div class="articles-overview__list" *ngSwitchCase="false">
                <ng-container *ngFor="let section of allArticlesInSections; let i = index;">
                    <ng-container [ngSwitch]="i % 4">

                        <!-- MOSAIC LTR -->
                        <ng-container *ngSwitchCase="0">
                            <div class="articles-overview__list-grid--mosaic mosaic-grid--ltr">
                                <ng-container *ngFor="let spot of section; let i = index;" [ngSwitch]="i">
                                    <ng-container *ngIf="spot">
                                        <div class="mosaic-spot--large" *ngSwitchCase="0">
                                            <ng-container *ngTemplateOutlet="SpotItem; context: { item: spot, size: 'large' }"></ng-container>
                                        </div>
                                        <div class="mosaic-spot--small" [ngClass]="{
                                            'mosaic-spot--small-first': i === 1,
                                            'mosaic-spot--small-second': i === 2
                                        }" *ngSwitchDefault>
                                            <ng-container *ngTemplateOutlet="SpotItem; context: { item: spot, size: 'small' }"></ng-container>
                                        </div>
                                    </ng-container>
                                </ng-container>
                            </div>
                        </ng-container>

                        <!-- GRID ROW -->
                        <ng-container *ngSwitchCase="1">
                            <div class="articles-overview__list-grid--row">
                                <ng-container *ngFor="let spot of section">
                                    <ng-container *ngIf="spot">
                                        <ng-container *ngTemplateOutlet="SpotItem; context: { item: spot, size: 'small' }"></ng-container>
                                    </ng-container>
                                </ng-container>
                            </div>
                        </ng-container>

                        <!-- MOSAIC RTL -->
                        <ng-container *ngSwitchCase="2">
                            <div class="articles-overview__list-grid--mosaic mosaic-grid--rtl">
                                <ng-container *ngFor="let spot of section; let i = index;" [ngSwitch]="i">
                                    <ng-container *ngIf="spot">
                                        <div class="mosaic-spot--large" *ngSwitchCase="1">
                                            <ng-container *ngTemplateOutlet="SpotItem; context: { item: spot, size: 'large' }"></ng-container>
                                        </div>
                                        <div class="mosaic-spot--small" [ngClass]="{
                                            'mosaic-spot--small-first': i === 0,
                                            'mosaic-spot--small-second': i === 2
                                        }" *ngSwitchDefault>
                                            <ng-container *ngTemplateOutlet="SpotItem; context: { item: spot, size: 'small' }"></ng-container>
                                        </div>
                                    </ng-container>
                                </ng-container>
                            </div>
                        </ng-container>

                        <!-- GRID ROW -->
                        <div class="articles-overview__list-grid--row" *ngSwitchCase="3">
                            <ng-container *ngFor="let spot of section">
                                <ng-container *ngIf="spot">
                                    <ng-container *ngTemplateOutlet="SpotItem; context: { item: spot, size: 'small' }"></ng-container>
                                </ng-container>
                            </ng-container>
                        </div>
                    </ng-container>
                </ng-container>

                <div class="no-articles-to-show" *ngIf="!showingArticlesAmount">
                    <div>
                        <h5>{{ 'articles.no_articles_found_header' | translate }}</h5>
                    </div>
                </div>
            </div>

            <!-- TODO: ADD BACK WHEN LINKS HAVE BEEN FIXED -->
            <div class="articles-spot__bottom" *ngIf="data?.articleSpotBottomLink?.url">
                <a [routerLink]="data?.articleSpotBottomLink?.url" [queryParams]="data?.articleSpotBottomLink?.urlParams" class="button--primary button--themed">
                    {{data?.articleSpotBottomLink?.name}}
                </a>
            </div>
        </ng-container>

        <!-- TEMPLATES -->
        <ng-template #SpotItem let-spot="item" let-size="size">
            <a [routerLink]="[spot?.url]" class="article-spot-item">
                <div class="article-spot-item__image-wrapper">
                    <app-responsive-image
                        [image]="spot?.introImage"
                        [breakpoints]="size === 'small' ? smallImageBreakpoints : largeImageBreakpoints"
                        class="article-spot-item__image">
                    </app-responsive-image>
                </div>
                <div class="article-spot-item__content">
                    <div class="article-spot-item__info">
                        <div class="article-spot-item__tags" *ngIf="spot?.categoryTags || spot?.vehicleBrandTags">
                            <div *ngFor="let tag of spot?.categoryTags; let i = index" class="article__info-tag">
                                <div *ngIf="i === 0">
                                    {{ tag?.displayName }}
                                </div>
                            </div>
                            <div *ngFor="let tag of spot?.vehicleBrandTags; let i = index" class="article__info-tag">
                                <div *ngIf="i === 0">
                                    {{ tag?.displayName }}
                                </div>
                            </div>
                        </div>
                        <div class="article-spot-item__date">
                            <div class="article-spot-item__info-seperator">|</div>
                            <time [attr.datetime]="spot?.articleCreatedDate">{{spot?.createdDate | date: 'dd. MMM yyyy' }}</time>
                        </div>
                    </div>
                    <h2 [appColonSplit]="spot?.title" class="article-spot-item__content-header">{{spot?.title}}</h2>
                    <p class="article-spot-item__content-text" *ngIf="size === 'large'">
                        {{spot?.subTitle}}
                    </p>
                </div>
            </a>
        </ng-template>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})

class ArticlesSpotComponent implements OnInit, OnDestroy {
    @Input() data: Cms.ArticlesSpot;
    @Input() appColonSplit: string;

    private readonly unsubscribe = new Subject<void>();

    allArticleSpots: Cms.ArticleDto[] = []; // All articles, unformatted
    allArticlesInSections: ArticleGroup[] = []; // All articles, divided into sections (chunks)
    allArticlesAmount: number; // How many articles match active filtering
    showingArticlesAmount: number; // How many articles are currently displayed
    isFetchingArticles = true;

    articleMakesTagList: Cms.ArticleTagNavigationDto[] = [];

    smallImageBreakpoints: IImageBreakpoints[];
    largeImageBreakpoints: IImageBreakpoints[];

    skip = 0;
    take: number = ARTICLE_ROWS_AMOUNT * 3;

    constructor(
        private contentService: ContentService,
        private settingsService: SettingsService,
        private location: Location,
        private cdr: ChangeDetectorRef
    ) {
            this.smallImageBreakpoints = [
                {
                    breakpoint: StyleBreakpoints.Implicit,
                    width: 360
                },
                {
                    breakpoint: StyleBreakpoints.Xsmall,
                    width: 210
                },
                {
                    breakpoint: StyleBreakpoints.Small,
                    width: 360
                },
                {
                    breakpoint: StyleBreakpoints.Medium,
                    width: 360
                },
                {
                    breakpoint: StyleBreakpoints.Large,
                    width: 360,
                },
                {
                    breakpoint: StyleBreakpoints.Xlarge,
                    width: 500,
                }
            ];

            this.largeImageBreakpoints = [
                {
                    breakpoint: StyleBreakpoints.Xsmall,
                    width: 420
                },
                {
                    breakpoint: StyleBreakpoints.Small,
                    width: 750
                },
                {
                    breakpoint: StyleBreakpoints.Medium,
                    width: 630
                },
                {
                    breakpoint: StyleBreakpoints.Large,
                    width: 750,
                },
                {
                    breakpoint: StyleBreakpoints.Xlarge,
                    width: 865,
                }
            ];
        }

    ngOnInit() {
        this.transformArticleTags();
        this.getArticles();
    }

    /**
     * Retrieves articles from the CMS based on the given parameters, and updates the local state.
     *
     * @remarks
     * This function is called when the component is initialized, and when the user requests more articles.
     * It fetches the next set of articles, and updates the local state with the new data.
     * It also generates a new URL based on the current filters and the new skip value.
     * In terms of formatting article data, it will be divided into "chunks" of 3 articles.
     * This is to create the layout of the articles overview page, where the layout is divided into rows,
     * and each row has different layout.
     */
    getArticles() {
        if (this.data) {
            const take = ARTICLE_ROWS_AMOUNT * 4;
            const makes = this.articleMakesTagList.map((make) => make.id) ?? [];

            this.isFetchingArticles = true;

            this.contentService.getArticles(this.skip, take, [], makes).subscribe((articlesData) => {
                this.allArticleSpots = this.allArticleSpots.concat(articlesData.articles);

                // Remove articles that are the same url as current page
                this.allArticleSpots = this.allArticleSpots.filter((article) => {
                    return this.location.path().split('/').pop() !== article.url;
                });

                this.allArticleSpots.length = 6;

                // Sort articles by date
                this.allArticleSpots = sortObjectArrayByDateProperty(this.allArticleSpots, 'createdDate', false);

                this.allArticlesAmount = Number(articlesData.totalItems);
                this.showingArticlesAmount = this.allArticleSpots.length;
                this.skip = this.showingArticlesAmount;

                // Create article rows data
                this.allArticlesInSections = chunksArray(this.allArticleSpots, 3);

                // Remove empty chunks
                for (let i = 0; i < this.allArticlesInSections.length; i++) {
                    if (!this.allArticlesInSections[i][0]) {
                        this.allArticlesInSections.splice(i, 1);
                    }
                }

                this.isFetchingArticles = false;

                this.cdr.markForCheck();
            });
        } else {
            console.error('[BN error: No MAKE selected for articles spot]');
            this.isFetchingArticles = false;
        }
    }

    transformArticleTags() {
       // Vehicle Make Tags
        this.articleMakesTagList = this.data?.articleMakeTags?.map((tag: TempAny) => ({
            displayName: tag.tagDisplayName,
            id: tag.id,
            key: tag.name.toLowerCase(),
            param: {
                [ArticleFilterTypes.MAKE]: tag.name.toLowerCase(),
            }
        })) ?? [];
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}

@NgModule({
    declarations: [ArticlesSpotComponent],
    imports: [
        CommonModule,
        SectionHeaderModule,
        RouterModule,
        UtilsModule,
        TranslateModule.forChild(),
        ComponentLoaderModule
    ]
})
class ArticlesSpotModule {}

export const LazySpotComponent = ArticlesSpotComponent;
